import React, { useState, useRef, useEffect } from "react";
import { useNotepadStore } from "../store/useNotepadStore";
import { useSlideStore } from "@/features/tutor/store/useSlideStore";
import { ASSESSMENT_KEYS } from "@/features/tutor/constants/assessmentTypes";

const ASSESSMENT_TYPE_SET = new Set(ASSESSMENT_KEYS);

const isAssessmentSlide = (s: {
  metadata?: { slide_type?: string };
  slide_type?: string;
}) => {
  const t = s.metadata?.slide_type ?? s.slide_type;
  return t != null && ASSESSMENT_TYPE_SET.has(t);
};

export function NotepadPageNav() {
  const { pages, currentPageIndex, isLimitedMode } = useNotepadStore();
  const switchToPage = useNotepadStore((s) => s.switchToPage);
  const currentSlideIndex = useSlideStore((s) => s.currentSlideIndex);
  const slides = useSlideStore((s) => s.slides);

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (isLimitedMode || pages.length <= 1) return null;

  const allSlides = [...(slides ?? [])].sort(
    (a: any, b: any) => (a.slide_index ?? 0) - (b.slide_index ?? 0),
  );
  const contentSlides = allSlides.filter((s: any) => !isAssessmentSlide(s));
  const currentPosition =
    contentSlides.findIndex((s: any) => s.slide_index === currentSlideIndex) +
      1 || 1;

  const latestPageIndex = Math.max(...pages);
  const isOnLatest = currentPageIndex === latestPageIndex;

  const triggerLabel = isOnLatest
    ? `${currentPosition}.${latestPageIndex + 1}`
    : `${currentPosition}.${currentPageIndex + 1}`;

  const sortedPages = [...pages].sort((a, b) => b - a);
  const olderPages = sortedPages.filter((p) => p !== latestPageIndex);

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={() => setIsOpen((v) => !v)}
        // Warm parchment tab matching the toolbar palette (canvas.css
        // .tlui-main-toolbar__tools: #efe1c0 surface, #6b5836 text), with a
        // soft top highlight so it reads slightly raised off the paper.
        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[13px] font-semibold transition-[filter] hover:brightness-105 focus:outline-none pointer-events-auto"
        style={{
          color: "#6b5836",
          backgroundImage:
            "linear-gradient(180deg, #f5ead0 0%, #efe1c0 55%, #e7d6ae 100%)",
          boxShadow:
            "inset 0 1px 0 rgba(255,255,255,0.55), 0 2px 6px rgba(120,96,44,0.22)",
        }}
      >
        {triggerLabel}
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
        >
          <path
            d="M2 4l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 min-w-[160px] bg-white rounded-xl border border-gray-200 shadow-lg z-[220] overflow-hidden pointer-events-auto">
          {/* Latest page */}
          <button
            onClick={() => {
              switchToPage(latestPageIndex);
              setIsOpen(false);
            }}
            className="w-full flex items-center justify-between px-3 py-2 text-[13px] text-left transition-colors hover:bg-gray-50 font-medium text-gray-700"
          >
            <span>
              {currentPosition}.{latestPageIndex + 1}
            </span>
          </button>

          {olderPages.length > 0 && (
            <>
              <div className="px-3 py-1 text-[11px] text-gray-400 italic font-normal border-t border-gray-100">
                Previous Notes
              </div>
              {olderPages.map((pageIdx) => (
                <button
                  key={pageIdx}
                  onClick={() => {
                    switchToPage(pageIdx);
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center py-2 text-[13px] text-left font-medium text-gray-600 transition-all rounded-md hover:bg-gray-100 hover:pl-[17px] px-3"
                >
                  {currentPosition}.{pageIdx + 1}
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
